import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import KdsAccessGate from './KdsAccessGate';
import { getOfflineAccountScope, scopedOfflineKey, isOfflineScopeCurrent, newOfflineId, readOfflineEntries } from './scopedOfflineQueue';

const POLL_MS = 4000;
const SETTINGS_POLL_MS = 15000;
const OFFLINE_QUEUE_KEY = 'kds_offline_queue_v1';
const UNDO_TIMEOUT_MS = 8000;
const CRITICAL_REPEAT_MS = 30000;

const TYPE_FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'sur_place', label: 'Sur place' },
  { id: 'a_emporter', label: 'À emporter' },
  { id: 'click_collect', label: 'Click & Collect' },
];

const DEFAULT_SCREENS = {
  incoming: true,
  prep: true,
  ready: true,
  assembly: false,
  dispatch: false,
};

function normalizeKdsScreens(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ...DEFAULT_SCREENS };
  }
  return {
    incoming: value.incoming !== undefined ? !!value.incoming : DEFAULT_SCREENS.incoming,
    prep: value.prep !== undefined ? !!value.prep : DEFAULT_SCREENS.prep,
    ready: value.ready !== undefined ? !!value.ready : DEFAULT_SCREENS.ready,
    assembly: value.assembly !== undefined ? !!value.assembly : DEFAULT_SCREENS.assembly,
    dispatch: value.dispatch !== undefined ? !!value.dispatch : DEFAULT_SCREENS.dispatch,
  };
}

function getOrderType(order) {
  const method = String(order?.paymentMethod || '').toLowerCase();
  const comment = String(order?.customerComment || '').toLowerCase();
  if (method === 'cb_online' || comment.includes('click') || comment.includes('collect')) {
    return 'click_collect';
  }
  if (comment.includes('sur place')) {
    return 'sur_place';
  }
  return 'a_emporter';
}

function isClickCollectPaid(order) {
  return String(order?.paymentStatus || '').trim().toLowerCase() === 'paid';
}

function getOrderAgeSeconds(order, nowTs = Date.now()) {
  const startedAt = new Date(order?.orderedAt || order?.createdAt || Date.now()).getTime();
  return Math.max(0, Math.floor((nowTs - startedAt) / 1000));
}

function formatTimer(seconds) {
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

function snapshotOrder(order, zone = 'all') {
  return {
    orderId: Number(order.id),
    previousOrderStatus: String(order?.status || 'en_cours').toLowerCase(),
    previousItems: (order?.OrderItems || []).filter((item) => zone === 'all' || String(item.productionZoneId) === String(zone)).map((item) => ({
      id: Number(item.id),
      status: String(item?.kitchenWorkflowStatus || 'pending').toLowerCase(),
    })),
  };
}

function getScreenParam() {
  const params = new URLSearchParams(window.location.search);
  return String(params.get('screen') || 'all').trim().toLowerCase();
}

function getZoneParam() {
  const params = new URLSearchParams(window.location.search);
  return String(params.get('zone') || '').trim();
}

function getColumnsByMode(mode, enabledScreens) {
  const allColumns = [
    { id: 'pending', title: 'Nouvelles commandes', action: 'COMMENCER', screen: 'incoming' },
    { id: 'preparing', title: 'En préparation', action: 'PRÊT', screen: 'prep' },
    { id: 'ready', title: 'Prêtes', action: 'TERMINER', screen: 'ready' },
  ];
  if (mode === 'dual') {
    return allColumns.filter((col) => ['pending', 'preparing'].includes(col.id) && enabledScreens[col.screen]);
  }
  return allColumns.filter((col) => enabledScreens[col.screen]);
}

function getColumnForOrder(order, screenParam = 'all', zone = 'all') {
  const status = String(order?.status || '').toLowerCase();
  const lineStatuses = (order?.OrderItems || [])
    .filter((item) => zone === 'all' || String(item.productionZoneId) === String(zone))
    .map((item) => String(item?.kitchenWorkflowStatus || 'pending').toLowerCase());
  const allDispatched = lineStatuses.length > 0 && lineStatuses.every((entry) => entry === 'dispatched');
  const allReadyOrLater = lineStatuses.length > 0 && lineStatuses.every((entry) => ['ready', 'assembled', 'dispatched'].includes(entry));
  const hasInProgress = lineStatuses.some((entry) => entry === 'in_progress');

  if (screenParam === 'assembly') {
    if (allReadyOrLater && lineStatuses.some((entry) => entry === 'ready')) {
      return 'ready';
    }
    return 'hidden';
  }
  if (screenParam === 'dispatch') {
    if (allReadyOrLater && !allDispatched) {
      return 'ready';
    }
    return 'hidden';
  }

  if (status === 'terminee' || allDispatched) return 'done';
  if ((zone === 'all' && status === 'prete') || allReadyOrLater) return 'ready';
  if (hasInProgress) return 'preparing';
  return 'pending';
}

function CuisineWorkspace({ access }) {
  const request = access.request;
  const [offlineScope] = useState(getOfflineAccountScope);
  const queueKey = scopedOfflineKey(OFFLINE_QUEUE_KEY, offlineScope);
  const readQueue = useCallback(() => readOfflineEntries(queueKey), [queueKey]);
  const saveQueue = useCallback((entries) => localStorage.setItem(queueKey, JSON.stringify(entries)), [queueKey]);
  const replayRunningRef = useRef(false);
  const hasLegacyQueue = readOfflineEntries(OFFLINE_QUEUE_KEY).length > 0;
  const [orders, setOrders] = useState([]);
  const [zones, setZones] = useState([]);
  const [station, setStation] = useState(() => access.mode === 'licensed' && access.device?.productionZoneId != null ? String(access.device.productionZoneId) : 'all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [nowTick, setNowTick] = useState(Date.now());
  const [busyOrderId, setBusyOrderId] = useState(null);
  const [offlineQueueCount, setOfflineQueueCount] = useState(readQueue().length);
  const [offlineQueueStatus, setOfflineQueueStatus] = useState({
    pending: readQueue().length,
    failed: 0,
    lastSyncedAt: 0,
  });
  const [undoState, setUndoState] = useState(null);
  const [kdsSettings, setKdsSettings] = useState({
    kdsScreenMode: 'single',
    kdsEnabledScreensJson: { ...DEFAULT_SCREENS },
    kdsScreenZoneMapJson: {
      incoming: '',
      prep: '',
      ready: '',
      assembly: '',
      dispatch: '',
    },
    kdsWarnThresholdSeconds: 180,
    kdsLateThresholdSeconds: 600,
    kdsSoundEnabled: true,
  });
  const knownOpenOrdersRef = useRef(new Set());
  const lastCriticalAlertRef = useRef(0);
  const undoTimeoutRef = useRef(null);

  const screenParam = useMemo(() => getScreenParam(), []);
  const zoneParam = useMemo(() => getZoneParam(), []);
  const licensedZone = access.mode === 'licensed' && access.device?.productionZoneId != null ? String(access.device.productionZoneId) : '';

  const beep = useCallback(() => {
    try {
      const context = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gain.gain.value = 0.03;
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.18);
    } catch (e) {
      // no-op
    }
  }, []);

  const enqueueOfflineAction = useCallback((entry) => {
    const current = readQueue();
    const next = [...current, { ...entry, id: newOfflineId(), accountScope: offlineScope, queuedAt: Date.now() }];
    saveQueue(next);
    setOfflineQueueCount(next.length);
    setOfflineQueueStatus((currentStatus) => ({ ...currentStatus, pending: next.length }));
  }, [offlineScope, readQueue, saveQueue]);

  const processOfflineQueue = useCallback(async () => {
    if (replayRunningRef.current || !isOfflineScopeCurrent(offlineScope)) return;
    replayRunningRef.current = true;
    let failed = 0;
    try {
      const queue = readQueue();
      for (const action of queue) {
        if (!isOfflineScopeCurrent(offlineScope)) break;
        if (!action.id || action.accountScope !== offlineScope || action.blocked) continue;
        try {
          const response = await request(action.path.replace(/^\/api\/orders(?=\/)/, '/api/kds/orders'), {
            method: action.method || 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(action.body || {}),
          });
          if (response.ok) {
            // Read again: a new user action may have arrived while the request was pending.
            saveQueue(readQueue().filter((entry) => entry.id !== action.id));
          } else {
            failed += 1;
            if (response.status >= 400 && response.status < 500) {
              saveQueue(readQueue().map((entry) => entry.id === action.id ? { ...entry, blocked: true, status: response.status } : entry));
            }
          }
        } catch (failure) {
          if (failure.kdsAccessDenied) break;
          failed += 1;
          break;
        }
      }
      const pending = readQueue().length;
      setOfflineQueueCount(pending);
      setOfflineQueueStatus({ pending, failed, lastSyncedAt: Date.now() });
    } finally {
      replayRunningRef.current = false;
    }
  }, [offlineScope, readQueue, saveQueue, request]);

  const fetchZones = useCallback(async () => {
    const response = await request('/api/kds/production-zones');
    if (!response.ok) {
      throw new Error('Impossible de charger les zones.');
    }
    const payload = await response.json();
    setZones(Array.isArray(payload) ? payload : []);
  }, [request]);

  const fetchKdsSettings = useCallback(async () => {
    const response = await request('/api/kds/settings');
    if (!response.ok) {
      throw new Error('Impossible de charger les réglages KDS.');
    }
    const payload = await response.json();
    setKdsSettings({
      kdsScreenMode: ['single', 'dual', 'full'].includes(String(payload?.kdsScreenMode || '').trim())
        ? String(payload.kdsScreenMode).trim()
        : 'single',
      kdsEnabledScreensJson: normalizeKdsScreens(payload?.kdsEnabledScreensJson),
      kdsScreenZoneMapJson: {
        incoming: String(payload?.kdsScreenZoneMapJson?.incoming || '').trim(),
        prep: String(payload?.kdsScreenZoneMapJson?.prep || '').trim(),
        ready: String(payload?.kdsScreenZoneMapJson?.ready || '').trim(),
        assembly: String(payload?.kdsScreenZoneMapJson?.assembly || '').trim(),
        dispatch: String(payload?.kdsScreenZoneMapJson?.dispatch || '').trim(),
      },
      kdsWarnThresholdSeconds: Math.max(30, Number(payload?.kdsWarnThresholdSeconds || 180)),
      kdsLateThresholdSeconds: Math.max(60, Number(payload?.kdsLateThresholdSeconds || 600)),
      kdsSoundEnabled: payload?.kdsSoundEnabled !== false,
    });
  }, [request]);

  const fetchOrders = useCallback(async () => {
    const response = await request('/api/kds/orders');
    if (!response.ok) {
      throw new Error('Impossible de charger les commandes cuisine.');
    }
    const payload = await response.json();
    const nextOrders = Array.isArray(payload) ? payload.filter((order) => {
      const status = String(order?.status || '').toLowerCase();
      if (status === 'annulee' || status === 'terminee') {
        return false;
      }
      const type = getOrderType(order);
      if (type === 'click_collect' && !isClickCollectPaid(order)) {
        return false;
      }
      return true;
    }) : [];

    const nextOpenSet = new Set(nextOrders.map((order) => Number(order.id)));
    const hasNew = nextOrders.some((order) => !knownOpenOrdersRef.current.has(Number(order.id)));
    if (kdsSettings.kdsSoundEnabled && knownOpenOrdersRef.current.size > 0 && hasNew) {
      beep();
    }
    knownOpenOrdersRef.current = nextOpenSet;
    setOrders(nextOrders);
  }, [beep, kdsSettings.kdsSoundEnabled, request]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await Promise.all([fetchZones(), fetchOrders(), fetchKdsSettings(), processOfflineQueue()]);
        if (alive) setError('');
      } catch (loadError) {
        if (alive) setError(loadError.message || 'Erreur de chargement KDS.');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    const pollId = window.setInterval(() => {
      fetchOrders().catch((pollError) => setError(pollError.message || 'Erreur KDS'));
      processOfflineQueue().catch(() => {});
    }, POLL_MS);
    const settingsPollId = window.setInterval(() => {
      fetchKdsSettings().catch(() => {});
    }, SETTINGS_POLL_MS);
    const tickId = window.setInterval(() => setNowTick(Date.now()), 1000);

    return () => {
      alive = false;
      window.clearInterval(pollId);
      window.clearInterval(settingsPollId);
      window.clearInterval(tickId);
      if (undoTimeoutRef.current) {
        window.clearTimeout(undoTimeoutRef.current);
      }
    };
  }, [fetchOrders, fetchZones, fetchKdsSettings, processOfflineQueue]);

  const stationTabs = useMemo(() => {
    const base = [{ id: 'all', label: 'Toutes' }];
    const mapped = zones.map((zone) => ({ id: String(zone.id), label: zone.name || `Zone ${zone.id}` }));
    return [...base, ...mapped];
  }, [zones]);

  const stationLock = useMemo(() => {
    if (licensedZone) return licensedZone;
    if (zoneParam) {
      return zoneParam;
    }
    if (screenParam && screenParam !== 'all') {
      const mapped = String(kdsSettings?.kdsScreenZoneMapJson?.[screenParam] || '').trim();
      if (mapped) {
        return mapped;
      }
    }
    return '';
  }, [kdsSettings?.kdsScreenZoneMapJson, screenParam, zoneParam, licensedZone]);

  const activeZoneLabel = useMemo(() => {
    const zoneId = String(stationLock || station || '').trim();
    if (!zoneId || zoneId === 'all') {
      return 'Toutes zones';
    }
    const zone = zones.find((entry) => String(entry.id) === zoneId);
    return zone?.name || `Zone ${zoneId}`;
  }, [station, stationLock, zones]);

  const activeScreenLabel = useMemo(() => {
    const map = {
      all: 'Global',
      incoming: 'Nouvelles commandes',
      prep: 'Preparation',
      ready: 'Pretes',
      assembly: 'Assemblage',
      dispatch: 'Dispatch',
    };
    return map[String(screenParam || 'all')] || 'Global';
  }, [screenParam]);

  useEffect(() => {
    if (stationLock) {
      setStation(stationLock);
    }
  }, [stationLock]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (typeFilter !== 'all' && getOrderType(order) !== typeFilter) return false;
      const effectiveStation = stationLock || station;
      if (effectiveStation === 'all') return true;
      return (order?.OrderItems || []).some((item) => String(item?.productionZoneId || '') === String(effectiveStation));
    });
  }, [orders, station, stationLock, typeFilter]);

  const grouped = useMemo(() => {
    const columns = { pending: [], preparing: [], ready: [] };
    filteredOrders.forEach((order) => {
      const column = getColumnForOrder(order, screenParam, stationLock || station);
      if (columns[column]) columns[column].push(order);
    });
    Object.keys(columns).forEach((key) => {
      columns[key].sort((a, b) => new Date(a?.orderedAt || 0).getTime() - new Date(b?.orderedAt || 0).getTime());
    });
    return columns;
  }, [filteredOrders, screenParam, stationLock, station]);

  const safeSetUndo = useCallback((orderSnapshot, updatedOrder) => {
    if (undoTimeoutRef.current) {
      window.clearTimeout(undoTimeoutRef.current);
    }
    const changedIds = new Set(orderSnapshot.previousItems.map((item) => item.id));
    const next = {
      ...orderSnapshot,
      nextItems: (updatedOrder?.OrderItems || []).filter((item) => changedIds.has(Number(item.id))).map((item) => ({
        id: Number(item.id), status: String(item.kitchenWorkflowStatus || 'pending').toLowerCase(),
      })),
      expiresAt: Date.now() + UNDO_TIMEOUT_MS,
    };
    setUndoState(next);
    undoTimeoutRef.current = window.setTimeout(() => setUndoState(null), UNDO_TIMEOUT_MS);
  }, []);

  const applyOrderStatus = useCallback(async (order, status) => {
    if (!isOfflineScopeCurrent(offlineScope)) { setError('Compte modifié : rechargez la vue cuisine.'); return; }
    const snapshot = snapshotOrder(order);
    setBusyOrderId(Number(order.id));
    try {
      const response = await request(`/api/kds/orders/${order.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, screen: screenParam || 'all' }),
      });
      const payload = await response.json();
      if (!response.ok) {
        setError(payload?.error || 'Mise à jour statut refusée.');
        return;
      }
      setOrders((current) => current.map((entry) => (Number(entry.id) === Number(payload.id) ? payload : entry)));
      safeSetUndo(snapshot, payload);
      setError('');
    } catch (statusError) {
      if (statusError.kdsAccessDenied) return;
      enqueueOfflineAction({ path: `/api/orders/${order.id}/status`, method: 'PATCH', body: { status, screen: screenParam || 'all' } });
      setError(`${statusError.message || 'Erreur KDS'} Action mise en file hors-ligne.`);
    } finally {
      setBusyOrderId(null);
    }
  }, [enqueueOfflineAction, offlineScope, safeSetUndo, screenParam, request]);

  const applyZoneStatus = useCallback(async (order, status) => {
    if (station === 'all' && !['assembly', 'dispatch'].includes(screenParam)) return;
    if (!isOfflineScopeCurrent(offlineScope)) { setError('Compte modifié : rechargez la vue cuisine.'); return; }
    const snapshot = snapshotOrder(order, station);
    setBusyOrderId(Number(order.id));
    try {
      const response = await request(`/api/kds/orders/${order.id}/kitchen-zone-status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zoneId: station === 'all' ? null : Number(station), status, screen: screenParam || 'all' }),
      });
      const payload = await response.json();
      if (!response.ok) {
        setError(payload?.error || 'Mise à jour zone refusée.');
        return;
      }
      setOrders((current) => current.map((entry) => (Number(entry.id) === Number(payload.id) ? payload : entry)));
      safeSetUndo(snapshot, payload);
      setError('');
    } catch (updateError) {
      if (updateError.kdsAccessDenied) return;
      enqueueOfflineAction({
        path: `/api/orders/${order.id}/kitchen-zone-status`,
        method: 'PATCH',
        body: { zoneId: station === 'all' ? null : Number(station), status, screen: screenParam || 'all' },
      });
      setError(`${updateError.message || 'Erreur zone'} Action mise en file hors-ligne.`);
    } finally {
      setBusyOrderId(null);
    }
  }, [enqueueOfflineAction, offlineScope, safeSetUndo, screenParam, station, request]);

  const undoLastAction = useCallback(async () => {
    if (!isOfflineScopeCurrent(offlineScope)) { setError('Compte modifié : rechargez la vue cuisine.'); return; }
    if (!undoState?.orderId || Date.now() > Number(undoState.expiresAt || 0)) {
      setUndoState(null);
      return;
    }
    setBusyOrderId(Number(undoState.orderId));
    try {
      const response = await request(`/api/kds/orders/${undoState.orderId}/kitchen-undo`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          previousOrderStatus: undoState.previousOrderStatus,
          previousItems: undoState.previousItems,
          nextItems: undoState.nextItems,
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error || 'Undo cuisine impossible.');
      setOrders((current) => current.map((entry) => (Number(entry.id) === Number(payload.id) ? payload : entry)));
      setUndoState(null);
      setError('');
    } catch (undoError) {
      setError(undoError.message || 'Erreur undo cuisine.');
    } finally {
      setBusyOrderId(null);
    }
  }, [offlineScope, undoState, request]);

  const lateOrders = useMemo(
    () => filteredOrders.filter((order) => getOrderAgeSeconds(order, nowTick) >= kdsSettings.kdsLateThresholdSeconds),
    [filteredOrders, nowTick, kdsSettings.kdsLateThresholdSeconds]
  );

  const avgPrep = useMemo(() => {
    const withReady = orders.filter((order) => String(order.status || '').toLowerCase() === 'prete' && order.readyAt);
    if (!withReady.length) return 0;
    const avg = withReady.reduce((sum, order) => {
      const started = new Date(order.orderedAt || order.createdAt || Date.now()).getTime();
      const ended = new Date(order.readyAt).getTime();
      return sum + Math.max(0, Math.floor((ended - started) / 1000));
    }, 0) / withReady.length;
    return Math.floor(avg);
  }, [orders]);

  const visibleColumns = useMemo(() => {
    const enabledScreens = normalizeKdsScreens(kdsSettings.kdsEnabledScreensJson);
    if (screenParam && screenParam !== 'all') {
      if ((screenParam === 'assembly' || screenParam === 'dispatch') && enabledScreens[screenParam]) {
        return [{ id: 'ready', title: screenParam === 'assembly' ? 'Assemblage' : 'Dispatch', action: screenParam === 'assembly' ? 'ASSEMBLER' : 'REMETTRE', screen: screenParam }];
      }
      const mapped = getColumnsByMode('full', enabledScreens).filter((col) => col.screen === screenParam);
      return mapped;
    }
    return getColumnsByMode(kdsSettings.kdsScreenMode, enabledScreens);
  }, [kdsSettings.kdsEnabledScreensJson, kdsSettings.kdsScreenMode, screenParam]);

  const actionPolicy = useMemo(() => {
    if (screenParam === 'incoming') {
      return { pending: true, preparing: false, ready: false };
    }
    if (screenParam === 'prep') {
      return { pending: false, preparing: true, ready: false };
    }
    if (screenParam === 'assembly' || screenParam === 'dispatch') {
      return { pending: false, preparing: false, ready: true };
    }
    if (screenParam === 'ready') {
      return { pending: false, preparing: false, ready: true };
    }
    return { pending: true, preparing: true, ready: true };
  }, [screenParam]);

  function getUrgencyClass(seconds) {
    if (seconds >= kdsSettings.kdsLateThresholdSeconds) return 'is-late';
    if (seconds >= kdsSettings.kdsWarnThresholdSeconds) return 'is-warn';
    return 'is-fresh';
  }

  useEffect(() => {
    const hasCriticalLate = filteredOrders.some(
      (order) => getOrderAgeSeconds(order, nowTick) >= (kdsSettings.kdsLateThresholdSeconds + 300)
    );
    if (!hasCriticalLate || !kdsSettings.kdsSoundEnabled) {
      return;
    }
    const now = Date.now();
    if ((now - Number(lastCriticalAlertRef.current || 0)) >= CRITICAL_REPEAT_MS) {
      beep();
      lastCriticalAlertRef.current = now;
    }
  }, [beep, filteredOrders, kdsSettings.kdsLateThresholdSeconds, kdsSettings.kdsSoundEnabled, nowTick]);

  return (
    <main className="kds-page">
      <header className="kds-topbar">
        <div className="kds-title-wrap">
          <h1>Kitchen Display</h1>
          <p>Cuisine temps réel: caisse + borne + click & collect</p>
          <div className="kds-context-badges">
            <span className="kds-context-chip">{`Ecran: ${activeScreenLabel}`}</span>
            <span className="kds-context-chip">{`Zone: ${activeZoneLabel}`}</span>
          </div>
        </div>
        <div className="kds-top-right">
          <strong>{new Date(nowTick).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</strong>
          <button type="button" onClick={() => fetchOrders().catch((pollError) => setError(pollError.message || 'Erreur KDS'))}>Rafraîchir</button>
        </div>
      </header>

      {hasLegacyQueue ? <div role="status" className="kds-offline-banner">Anciennes actions hors ligne conservées, sans compte identifié : reprise automatique désactivée.</div> : null}
      {undoState && Date.now() <= Number(undoState.expiresAt || 0) ? (
        <div className="kds-undo-bar" role="status" aria-live="polite">
          <span>Action validée</span>
          <button type="button" onClick={undoLastAction} disabled={busyOrderId === Number(undoState.orderId)}>Annuler</button>
        </div>
      ) : null}

      {offlineQueueCount > 0 ? (
        <div className="kds-offline-banner" role="status" aria-live="polite">
          Hors ligne partielle: {offlineQueueStatus.pending} en attente · {offlineQueueStatus.failed} en echec.
        </div>
      ) : null}

      <section className="kds-filters">
        <div className="kds-filter-row">
          {stationTabs.map((tab) => (
            <button key={tab.id} type="button" className={station === tab.id ? 'is-active' : ''} onClick={() => setStation(tab.id)} disabled={!!stationLock}>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="kds-filter-row">
          {TYPE_FILTERS.map((filter) => (
            <button key={filter.id} type="button" className={typeFilter === filter.id ? 'is-active' : ''} onClick={() => setTypeFilter(filter.id)}>
              {filter.label}
            </button>
          ))}
        </div>
      </section>

      {error ? <div className="kds-alert">{error}</div> : null}

      <section className="kds-grid">
        {visibleColumns.map((column) => (
          <div key={`${column.id}-${column.screen}`} className={`kds-column is-${column.id}`}>
            <div className="kds-column-head">
              <h2>{column.title}</h2>
              <strong>{grouped[column.id].length}</strong>
            </div>
            <div className="kds-column-body">
              {grouped[column.id].length ? grouped[column.id].map((order) => {
                const ageSeconds = getOrderAgeSeconds(order, nowTick);
                const urgencyClass = getUrgencyClass(ageSeconds);
                const type = getOrderType(order);
                const stationItems = station === 'all'
                  ? (order?.OrderItems || [])
                  : (order?.OrderItems || []).filter((item) => String(item?.productionZoneId || '') === station);
                return (
                  <article key={order.id} aria-label={`Commande ${order.ticketNumber || order.id}`} className={`kds-card ${urgencyClass}`}>
                    <div className="kds-card-head">
                      <strong>#{String(order.ticketNumber || order.id)}</strong>
                      <div>
                        <small>{new Date(order.orderedAt || order.createdAt || nowTick).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
                        <b>{formatTimer(ageSeconds)}</b>
                      </div>
                    </div>
                    <div className="kds-card-type">{order.fulfillmentType === 'delivery' ? 'LIVRAISON UBER DIRECT' : type === 'sur_place' ? 'SUR PLACE' : type === 'a_emporter' ? 'À EMPORTER' : 'CLICK & COLLECT'}</div>
                    {order.fulfillmentType === 'delivery' ? <small className="kds-card-delivery-status">Coursier : {String(order.uberDirectStatus || 'après paiement').replaceAll('_', ' ')}</small> : null}
                    <div className="kds-card-items">
                      {stationItems.map((item) => (
                        <div key={item.id} className="kds-item-row">
                          <span>{Number(item.quantity || 0)} x {item.kitchenName || item.name}</span>
                          {item.selections?.length ? <small>{item.selections.map((entry) => entry?.name || '').filter(Boolean).join(', ')}</small> : null}
                        </div>
                      ))}
                    </div>
                    <div className="kds-card-actions">
                      {column.id === 'pending' ? (
                        <button type="button" disabled={!actionPolicy.pending || busyOrderId === Number(order.id)} onClick={() => (station === 'all' ? applyOrderStatus(order, 'en_cours') : applyZoneStatus(order, 'in_progress'))}>{column.action}</button>
                      ) : null}
                      {column.id === 'preparing' ? (
                        <button type="button" disabled={!actionPolicy.preparing || busyOrderId === Number(order.id)} onClick={() => (station === 'all' ? applyOrderStatus(order, 'prete') : applyZoneStatus(order, 'ready'))}>{column.action}</button>
                      ) : null}
                      {column.id === 'ready' ? (
                        <button
                          type="button"
                          disabled={!actionPolicy.ready || busyOrderId === Number(order.id)}
                          onClick={() => {
                            if (screenParam === 'assembly') {
                              applyZoneStatus(order, 'assembled');
                            } else if (screenParam === 'dispatch') {
                              applyZoneStatus(order, 'dispatched');
                            } else if (station !== 'all') {
                              applyZoneStatus(order, 'dispatched');
                            } else {
                              applyOrderStatus(order, 'terminee');
                            }
                          }}
                        >
                          {column.action}
                        </button>
                      ) : null}
                    </div>
                  </article>
                );
              }) : <div className="kds-empty">Aucune commande</div>}
            </div>
          </div>
        ))}

        <aside className="kds-sidepanel">
          <div className="kds-side-card">
            <h3>Commandes en retard</h3>
            <strong>{lateOrders.length}</strong>
            <div className="kds-late-list">
              {lateOrders.slice(0, 8).map((order) => (
                <div key={order.id}>
                  <span>#{String(order.ticketNumber || order.id)}</span>
                  <b>{formatTimer(getOrderAgeSeconds(order, nowTick))}</b>
                </div>
              ))}
            </div>
          </div>
          <div className="kds-side-card">
            <h3>Temps moyen préparation</h3>
            <strong>{formatTimer(avgPrep)}</strong>
          </div>
          <div className="kds-side-card">
            <h3>Aujourd’hui</h3>
            <strong>{orders.length}</strong>
            <small>commandes en cours</small>
          </div>
        </aside>
      </section>

      {loading ? <div className="kds-loading">Chargement KDS...</div> : null}
    </main>
  );
}

export default function CuisineView() {
  return <KdsAccessGate>{access => <CuisineWorkspace key={access.generation} access={access} />}</KdsAccessGate>;
}
